import DocumentHeader from 'Components/DocumentHeader';
import DocumentWrapper from 'Components/DocumentWrapper';
import PlatformConditional from 'Components/PlatformConditional';
import { FormContextProvider } from 'Containers/FormContextHandler';
import FormEditingHandler from 'Containers/FormEditingHandler';
import * as React from 'react';
import { useSelector } from 'react-redux';
import { filterByDocumentType } from 'Utility/documentHandling';
import { getHeaderId } from '../utils';
import Form from './Form';
import CarbonDioxideMeasurementHistory from './HistoryRowData/CarbonDioxideMeasurement';
import DiagnosticExaminationHistory from './HistoryRowData/DiagnosticExamination';
import NightOximetryHistory from './HistoryRowData/NightOximetry';
import SleepPolygraphyHistory from './HistoryRowData/SleepPolygraphy';
import SleepStudyHistory from './HistoryRowData/SleepStudy';
import SpirometryHistory from './HistoryRowData/Spirometry';

import type { TClpAndCnpTestsDocumentType, TRelevantDocumentType } from '../utils';

const History = ({
  documents,
  startEdit,
  view,
  setDiagnosticExamKey,
}: {
  documents: IDocumentWithTypeV2<TClpAndCnpTestsDocumentType>[];
  startEdit: StartEdit;
  view?: IView;
  setDiagnosticExamKey?: React.Dispatch<React.SetStateAction<TDiagnosticExamKeys | undefined>>;
}): React.JSX.Element => {
  const SleepPolygraphyDocs = filterByDocumentType(documents, 'sleepPolygraphy');
  const SleepStudyDocs = filterByDocumentType(documents, 'sleepStudy');
  const SpirometryDocs = filterByDocumentType(documents, 'spirometry');
  const NightOximetryDocs = filterByDocumentType(documents, 'nightOximetry');
  const CarbonDioxideMeasurementDocs = filterByDocumentType(documents, 'carbonDioxideMeasurement');
  const diagnosticExaminationDocs = filterByDocumentType(documents, 'diagnosticExamination');

  return (
    <>
      <PlatformConditional platform={'sleepApnea'}>
        <SleepPolygraphyHistory documents={SleepPolygraphyDocs} startEdit={startEdit} view={view} />
        <SleepStudyHistory documents={SleepStudyDocs} startEdit={startEdit} view={view} />
        <SpirometryHistory documents={SpirometryDocs} startEdit={startEdit} view={view} />
        <NightOximetryHistory documents={NightOximetryDocs} startEdit={startEdit} view={view} />
        <CarbonDioxideMeasurementHistory documents={CarbonDioxideMeasurementDocs} startEdit={startEdit} view={view} />
      </PlatformConditional>
      <PlatformConditional platform={'mgravis'}>
        <DiagnosticExaminationHistory
          documents={diagnosticExaminationDocs}
          startEdit={startEdit}
          setDiagnosticExamCode={setDiagnosticExamKey}
        />
      </PlatformConditional>
    </>
  );
};

const ClpAndCnpTests = ({ documents }: IOwnProps): React.JSX.Element => {
  const [diagnosticExamKey, setDiagnosticExamKey] = React.useState<TDiagnosticExamKeys | undefined>(undefined);
  const platform = useSelector((s: IState) => s.session.platforms?.selected);

  const clpAndCnpTestDocuments = documents.filter(d => d._type !== 'diagnosis' && d._type !== 'madTherapy');

  return (
    <FormEditingHandler name="clpAndCnpTests" documents={documents}>
      {(editing, startEdit, formData, view): React.JSX.Element => (
        <DocumentWrapper>
          <DocumentHeader
            name="clpAndCnpTests"
            headerId={getHeaderId(clpAndCnpTestDocuments, editing, view?.viewing, platform, diagnosticExamKey)}
            editing={editing}
            editButtons={<div />}
          />
          {editing || view?.viewing ? (
            <FormContextProvider context={{ formData, documents }}>
              <Form
                formData={formData}
                document={clpAndCnpTestDocuments.find(d => d._id === (editing || view?.viewing))}
                viewing={!!view?.viewing}
                documents={documents}
                diagnosticExamKey={diagnosticExamKey}
              />
            </FormContextProvider>
          ) : null}
          {!editing && !view?.viewing ? (
            <History
              documents={clpAndCnpTestDocuments}
              startEdit={startEdit}
              view={view}
              setDiagnosticExamKey={setDiagnosticExamKey}
            />
          ) : undefined}
        </DocumentWrapper>
      )}
    </FormEditingHandler>
  );
};

interface IOwnProps {
  documents: Array<IDocumentWithTypeV2<TRelevantDocumentType>>;
}

export default ClpAndCnpTests;
